import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Eye, EyeOff } from 'lucide-react';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 14px;
  transition: all 0.2s ease;
  pointer-events: none;
  font-family: ${({ theme }) => theme.metrics.font.family};
  background: #fff;
  padding: 0 4px;

  ${({ $float, theme }) =>
    $float
      ? css`
          top: -8px;
          font-size: 11px;
          color: ${theme.colors.primary.main};
          font-weight: ${theme.metrics.font.weight.semiBold};
        `
      : css`
          top: 50%;
          transform: translateY(-50%);
          font-size: ${theme.metrics.font.size.sm};
          color: #A0A8B4;
          font-weight: ${theme.metrics.font.weight.regular};
        `}

  ${({ $isTextarea, $float }) =>
    $isTextarea && !$float &&
    css`
      top: 16px;
      transform: none;
    `}

  ${({ $error, $float, theme }) =>
    $error && $float &&
    css`
      color: ${theme.colors.auxiliar.danger};
    `}
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin-left: 2px;
`;

// #72 (UI düzeltme demeti T6) — "ince varyant" (kullanıcı talimatı: "Text
// field, select gibi bileşenleri de ince varyantları ile değiştireceksin").
// Taban dolgu 14px 16px → 10px 14px (yükseklik ~48-52px'ten ~40-42px'e
// iner) — TEK yerden, uygulamadaki HER `TextInput` çağrı sitesi kod
// değişikliği olmadan bu daha yoğun (dense) hissi otomatik alır. Floating
// label'ın shrink konumu (`FloatingLabel`'ın `top:-8px`'i) box yüksekliğinden
// BAĞIMSIZ (wrapper'a göre mutlak konumlanıyor) — bu değişiklik T1'in #73
// düzeltmesini (komşu HintText çakışması, ayrı dosyalarda) ETKİLEMEZ.
const sharedStyles = css`
  width: 100%;
  padding: 10px 14px;
  padding-right: ${({ $hasToggle }) => ($hasToggle ? '44px' : '14px')};
  border: 1.5px solid ${({ theme, $error }) => ($error ? theme.colors.auxiliar.danger : '#E0E4E8')};
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(2, 145, 239, 0.1);
  }
`;

const StyledInput = styled.input`${sharedStyles}`;
const StyledTextarea = styled.textarea`
  ${sharedStyles}
  resize: vertical;
  min-height: ${({ rows }) => (rows || 4) * 24}px;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #A0A8B4;
  display: flex;
  padding: 4px;
  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

export default function TextInput({
  label,
  error,
  type = 'text',
  required = false,
  debounceMs = 0,
  onChange,
  onVisibilityChange,
  value,
  rows,
  ...props
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const debounceTimer = useRef(null);
  const isPassword = type === 'password';
  const isTextarea = type === 'textarea';
  const inputType = isPassword && showPassword ? 'text' : type === 'textarea' ? undefined : type;
  const hasValue = localValue !== '' && localValue !== null && localValue !== undefined;
  // Placeholder'lı alanda etiket DAİMA yukarıda durur — ortadaki etiket, boş
  // alanda native placeholder metniyle üst üste biner (MUI'nin shrink kuralı).
  const shouldFloat = focused || hasValue || Boolean(props.placeholder);

  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);

    if (debounceMs > 0) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        onChange?.(e);
      }, debounceMs);
    } else {
      onChange?.(e);
    }
  };

  const Component = isTextarea ? StyledTextarea : StyledInput;

  return (
    <Wrapper>
      <InputContainer>
        <Component
          type={inputType}
          value={localValue}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          $error={!!error}
          $hasToggle={isPassword}
          rows={rows}
          {...props}
        />
        {label && (
          <FloatingLabel $float={shouldFloat} $error={!!error} $isTextarea={isTextarea} className={shouldFloat ? 'label-float' : ''}>
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </FloatingLabel>
        )}
        {isPassword && (
          <ToggleButton
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const next = !showPassword;
              setShowPassword(next);
              onVisibilityChange?.(next);
            }}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </ToggleButton>
        )}
      </InputContainer>
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
}
